import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingCartIcon, TruckIcon, XIcon } from 'lucide-react';
import { CartLineItem } from './CartLineItem';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
export function CartDrawer() {
  const {
    items,
    isOpen,
    closeCart,
    subtotal,
    count
  } = useCart();
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeCart();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeCart]);
  return <AnimatePresence data-id="element-19" data-mp="22yzvx5tla5ja">
      {isOpen && <>
          <motion.div key="overlay" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={closeCart} className="fixed inset-0 z-50 bg-ink/40" aria-hidden="true" data-id="element-20" data-mp="22wdod66p7q12" />
          <motion.aside key="drawer" role="dialog" aria-modal="true" aria-label="Shopping cart" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease: [0.32, 0.72, 0, 1]
      }} className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white" data-id="element-21" data-mp="3k3zymvfeju68">
            <div className="flex h-16 items-center justify-between border-b border-line px-5" data-id="element-22" data-mp="6vwvpaj600rn">
              <h2 className="text-base font-bold text-ink" data-id="element-23" data-mp="3o4jy45ng79vj">
                Your cart <span className="font-medium text-muted" data-id="element-24" data-mp="3t3wsdbgnfj85">({count})</span>
              </h2>
              <button type="button" onClick={closeCart} aria-label="Close cart" className="flex h-9 w-9 items-center justify-center rounded-lg text-ink hover:bg-surface" data-id="element-25" data-mp="2cpokmn0jfegk">
                <XIcon className="h-5 w-5" data-id="element-26" data-mp="o5r2woziszyk" />
              </button>
            </div>

            {items.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-27" data-mp="j9tpaqfn67ph">
                <ShoppingCartIcon className="h-10 w-10 text-ink/20" aria-hidden="true" data-id="element-28" data-mp="eeec80pgkfu4" />
                <p className="mt-4 text-base font-semibold text-ink" data-id="element-29" data-mp="9fju70zbevb2">Your cart is empty</p>
                <p className="mt-1 text-sm text-muted" data-id="element-30" data-mp="4jw2oexd1q2e">Browse best sellers or use the battery advisor.</p>
                <Link to="/shop" onClick={closeCart} className="mt-6 inline-flex h-11 items-center rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-31" data-mp="j2jtzyrz1gd">
                  Start shopping
                </Link>
              </div> : <>
                <div className="flex items-center gap-2 bg-surface px-5 py-3 text-xs font-medium text-ink" data-id="element-32" data-mp="1kxuqnxj0sjwq">
                  <TruckIcon className="h-4 w-4 text-success" aria-hidden="true" data-id="element-33" data-mp="1sfkjdz1yk5jh" />
                  Free shipping unlocked · Delivered within 1–2 business days
                </div>
                <ul className="flex-1 divide-y divide-line overflow-y-auto px-5" data-id="element-34" data-mp="225vm5i9wo8os">
                  {items.map(item => <CartLineItem key={item.key} item={item} onNavigate={closeCart} data-id="element-35" data-mp="274p2digkudsn" />)}
                </ul>
                <div className="border-t border-line p-5" data-id="element-36" data-mp="19judw1utgr0u">
                  <div className="flex items-baseline justify-between" data-id="element-37" data-mp="1ehccozf4dpv4">
                    <span className="text-base font-semibold text-ink" data-id="element-38" data-mp="1jf4fwpl7syem">Subtotal</span>
                    <span className="text-xl font-bold tracking-tight text-ink" data-id="element-39" data-mp="1oeh3jcjwu9i9">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted" data-id="element-40" data-mp="2utriglvkbyl1">
                    Incl. VAT · or 3 × {formatPrice(Math.round(subtotal / 3 * 100) / 100)} with Klarna
                  </p>
                  <Link to="/cart" onClick={closeCart} className="mt-4 flex h-12 w-full items-center justify-center rounded-lg bg-volt text-sm font-bold text-ink transition-colors duration-150 hover:bg-volt-hover" data-id="element-41" data-mp="2xeh366y3c7e1">
                    Go to checkout
                  </Link>
                  <button type="button" onClick={closeCart} className="mt-2 h-10 w-full text-sm font-medium text-ink hover:underline" data-id="element-42" data-mp="32gepxb5p9kbq">
                    Continue shopping
                  </button>
                </div>
              </>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}